// Meta Pixel (browser side). The backend sends the same events through the
// Conversions API with the same event_id (see the META CAPI section in
// server.py), and Meta deduplicates the pair. Like ga4.js/analytics.js,
// every call is best effort: a missing ID or a blocked script never breaks
// the app.
//
// Without REACT_APP_META_PIXEL_ID nothing loads or gets sent. In development,
// every event is also logged to the console ("[Meta Pixel] ...").

const PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID || '';
const IS_DEV = process.env.NODE_ENV === 'development';
const SENT_KEY_PREFIX = 'mipisto_meta_sent_';

function devLog(...args) {
  if (IS_DEV) console.info('[Meta Pixel]', ...args); // eslint-disable-line no-console
}

// Loads the Pixel base code once and sends the initial PageView. The app has
// no router (pages are switched by state, without changing the URL), so
// this is the only PageView. Calling it again is a no-op (StrictMode, HMR).
export function initMetaPixel() {
  try {
    if (!PIXEL_ID) {
      devLog('REACT_APP_META_PIXEL_ID is not set; Pixel disabled');
      return;
    }
    if (window.fbq) return;
    // The internal panel (/admin) shows user data: no Pixel there.
    if (window.location.pathname.startsWith('/admin')) return;
    /* eslint-disable */
    !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
    n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,
    document,'script','https://connect.facebook.net/en_US/fbevents.js');
    /* eslint-enable */
    // No automatic events: in a finance app we don't want the Pixel reading
    // button text or forms on its own. Only the events below are sent.
    window.fbq('set', 'autoConfig', false, PIXEL_ID);
    window.fbq('init', PIXEL_ID);
    window.fbq('track', 'PageView');
    devLog('PageView');
  } catch (_) { /* analytics should never break the app */ }
}

function alreadySent(eventId) {
  try { return !!localStorage.getItem(SENT_KEY_PREFIX + eventId); } catch (_) { return false; }
}

function markSent(eventId) {
  try { localStorage.setItem(SENT_KEY_PREFIX + eventId, '1'); } catch (_) { /* private mode etc. */ }
}

// Standard event with a deterministic eventID (built from server IDs:
// reg_<user_id>, purchase_<reference>...) so that:
//  - Meta deduplicates it against the same event sent by CAPI.
//  - It's never sent twice from this browser (refresh, re-render, StrictMode
//    double effects): it's remembered in localStorage by eventID.
// Never pass PII or financial data in params: only value/currency and the
// like.
export function trackMetaEvent(eventName, params = {}, eventId) {
  try {
    if (eventId) {
      if (alreadySent(eventId)) return;
      markSent(eventId);
    }
    devLog(eventName, params, eventId ? { eventID: eventId } : '');
    if (!PIXEL_ID || typeof window.fbq !== 'function') return;
    window.fbq('track', eventName, params, eventId ? { eventID: eventId } : undefined);
  } catch (_) { /* analytics should never break the app */ }
}

function readCookie(name) {
  try {
    const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : '';
  } catch (_) { return ''; }
}

// The Pixel's _fbp/_fbc cookies live on mipisto.net, but the backend is on
// another domain and never sees them. They're sent as headers on the calls
// that fire a CAPI event so the server event matches the same browser and
// ad click.
export function metaHeaders() {
  const headers = {};
  const fbp = readCookie('_fbp');
  const fbc = readCookie('_fbc');
  if (fbp) headers['X-Meta-Fbp'] = fbp;
  if (fbc) headers['X-Meta-Fbc'] = fbc;
  return headers;
}

// Browser-side Purchase, only for a payment the backend already confirmed
// as paid (billing/status -> last_purchase) and only if it's recent: an old
// purchase is never re-sent because someone lands on ?billing=success
// again. Returns true if the purchase is there (sent now or before), so the
// caller can stop retrying.
const PURCHASE_MAX_AGE_MS = 2 * 60 * 60 * 1000;

export function trackRecentPurchase(lastPurchase) {
  if (!lastPurchase?.event_id || !lastPurchase.paid_at) return false;
  const age = Date.now() - new Date(lastPurchase.paid_at).getTime();
  if (!(age >= 0 && age <= PURCHASE_MAX_AGE_MS)) return false;
  trackMetaEvent('Purchase', { value: lastPurchase.value, currency: lastPurchase.currency }, lastPurchase.event_id);
  return true;
}
